import React, { useState } from "react";
import { IoMdDownload } from "react-icons/io";
import { FaWindows } from "react-icons/fa";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import { TbCopy } from "react-icons/tb";


const CopyablePre = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const [title, setTitle] = useState("Copy to clipboard");

    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTitle("Copied!")
            setTimeout(() => {
                setTitle("Copy to clipboard");
                setCopied(false)
            }, 2000);
        });
    };

    return (
        <div className="position-relative d-inline-block">
            <pre
                className="bg-light p-2 border rounded mb-0 pe-5"
                style={{
                    whiteSpace: "pre-wrap",
                    display: "inline-block",
                    maxWidth: "100%",
                }}
            >
                {text}
            </pre>
            <button
                type="button"
                className="btn btn-sm position-absolute"
                style={{ top: "5px", right: "5px" }}
                onClick={handleCopy}
                title={title}
            >
                {copied ?
                    <IoCheckmarkDoneOutline className="mt-1" size={18} /> :
                    <TbCopy className="mt-1" size={18} />
                }
            </button>
        </div>
    );
};

const RfidReaderDetails = () => {
    return (
        <div className="container my-">
            <h2 className="mb-3 text-primary">RFID Reader Setup Instructions</h2>

            <ol className="mb-4 ps-3">
                <li className="mb-3">
                    <strong>1. Download the required files</strong>
                    <div className="d-flex flex-wrap gap-4 mt-2 ms-3">
                        <div className="d-flex align-items-center gap-2">
                            <code>P Com Driver</code>
                            <a
                                href="/RFIDReaderExe/P%20Com%20Driver.exe"
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary rounded-pill"
                                title="Download P Com Driver"
                            >
                                <IoMdDownload size={13} />
                            </a>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <code>ISPL RFID Local Agent</code>
                            <a
                                href="/RFIDReaderExe/ISPL%20RFID%20Local%20Agent.zip"
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary rounded-pill"
                                title="Download ISPL RFID Local Agent"
                            >
                                <IoMdDownload size={13}/>
                            </a>
                        </div>
                    </div>
                </li>


                <li className="mb-3">
                    <strong>2. Install the driver</strong>
                    <p className="mb-1 ms-3">
                        Double-click <code>P Com Driver.exe</code> and follow the on-screen
                        instructions to complete the installation.
                    </p>
                </li>

                <li className="mb-3">
                    <strong>3. Install the ISPL RFID Local Agent</strong>
                    <ul className="mb-1 ms-3" style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
                        <li>First, extract the downloaded ZIP file.</li>
                        <li>Then, double-click <code>ISPL RFID Local Agent.exe</code></li>
                        <li>Allow the installer and permit access when the firewall prompt appears.</li>
                    </ul>
                </li>

                <li className="mb-3">
                    <strong>4. Connect the RFID reader device</strong>
                    <p className="mb-1 ms-3">
                        Plug the RFID reader into any available USB port and wait for your
                        system to recognize it (you may see a red light turn on).
                    </p>
                </li>

                <li className="mb-3">
                    <strong>5. Set the ISPL RFID Local Agent to Auto-Run on Startup</strong>
                    <ol className="ms-4 mt-2" type="a" style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
                        <li className="mb-2">
                            <strong>Create a shortcut</strong>
                            <p className="mb-1">
                                Right-click <code>ISPL RFID Local Agent.exe</code>. On Windows
                                11 select <em>Show more options</em>, then click{" "}
                                <em>Create shortcut</em>.
                            </p>
                        </li>
                        <li className="mb-2">
                            <strong>Open the Startup folder</strong>
                            <p className="mb-1">
                                Press <kbd><FaWindows /></kbd> + <kbd>R</kbd> to open the Run dialog,
                                type <code>shell:startup</code>, and press <kbd>Enter</kbd>.
                            </p>
                        </li>
                        <li className="mb-2">
                            <strong>Move the shortcut</strong>
                            <p className="mb-1">
                                Go back to the folder containing the shortcut you created,
                                cut/copy it, and paste it into the Startup folder that opened.
                            </p>
                        </li>
                        <li>
                            <strong>Install and run the agent as a Windows service</strong>
                            <ol className="ms-4 mt-2" type="1">
                                <li className="mb-2">
                                    <strong>Open Command Prompt as Administrator</strong>
                                    <p className="mb-1">
                                        Click Start → type <em>cmd</em> → right-click{" "}
                                        <em>Command Prompt</em> → select{" "}
                                        <em className="border-bottom">Run as administrator</em>.
                                    </p>
                                </li>
                                <li className="mb-2">
                                    <strong>Navigate to the agent folder</strong>
                                    <p className="mb-1">
                                        <em>Example:</em>
                                    </p>
                                    <CopyablePre text={`cd "C:\\Users\\John\\Downloads\\ISPL RFID Local Agent"`} />
                                </li>
                                <li>
                                    <strong>Install and start the Windows service</strong>
                                    <p className="mb-1">Run this command:</p>
                                    <CopyablePre text={`"ISPL RFID Local Agent.exe" install && net start ISPLRFIDAgent`} />
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-3">
                    <strong>6. Test the setup</strong>
                    <ul className="ms-3 mt-2" style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
                        <li>Open the RFID scanning page in your browser.</li>
                        <li>Place the RFID tag near the RFID reader.</li>
                        <li>Click <strong>Scan Tag</strong> until it is detected.</li>
                        <li>You should hear a <strong>beep sound</strong> when detection is successful.</li>
                    </ul>
                </li>

            </ol>

            <div className="alert alert-info mb-3">
                Important: The ISPL RFID Local Agent must be running for the RFID Reader to work.
                <br />
                Tip: Check your system tray to confirm it’s running.
            </div>
        </div>
    );
};

export default RfidReaderDetails;
